import { Injectable, inject } from "@angular/core";
import { User, UserManager } from "oidc-client-ts";
import { jwtDecode } from "jwt-decode";
import { catchError, from, map, switchMap, throwError } from "rxjs";
import {
	HttpBackend,
	HttpClient,
	HttpHeaders,
	HttpParams,
	HttpErrorResponse,
} from "@angular/common/http";
import {
	ROOMS_LIB_CONFIG,
	RoomsLibConfig,
	RoomsUserProfile,
	UserAddress,
	UserInfoLoginReponse,
} from "@3volutionsag/ngx-rooms-lib";

import type { Observable } from "rxjs";
import type { JwtClaims, UserManagerSettings } from "oidc-client-ts";
import type { TokenResult } from "./token-result";

const APIKEY_GRANT_TYPE = "apikey";
const DEFAULT_SCOPES = ["openid", "profile", "rooms_api"].join(" ");

/**
 * Extension of PersonModel that includes token information needed by the authentication state
 */
export interface PersonModelWithTokens extends RoomsUserProfile {
	access_token: string;
	refresh_token?: string;
	token_type: string;
	expires_at: number;
	id_token?: string;
}

@Injectable({
	providedIn: "root",
})
export class OnSiteAuthService {
	private _roomsLibConfig?: RoomsLibConfig;

	private get roomsLibConfig() {
		if (!this._roomsLibConfig) {
			this._roomsLibConfig = inject(ROOMS_LIB_CONFIG);
		}

		return this._roomsLibConfig;
	}

	private http = inject(HttpBackend);

	private userManager: UserManager;

	constructor() {
		const redirectUri = this.roomsLibConfig.oauthConfig.redirectUri;
		const postRedirectUri = redirectUri ? `${new URL(redirectUri).origin}?ignore_acr=true` : "";

		const settings: UserManagerSettings = {
			authority: this.roomsLibConfig.oauthConfig.authority,
			client_id: this.roomsLibConfig.oauthConfig.clientId,
			redirect_uri: this.roomsLibConfig.oauthConfig.redirectUri,
			post_logout_redirect_uri: postRedirectUri,
			scope: this.roomsLibConfig.oauthConfig.scope ?? DEFAULT_SCOPES,
			response_type: "code",
			loadUserInfo: true,
		};
		this.userManager = new UserManager(settings);
	}

	private convertClaimsToUserProfile(claims: JwtClaims, expiresIn: number): RoomsUserProfile {
		const customClaims = claims as unknown as UserInfoLoginReponse;
		const standardClaims = claims as unknown as {
			name?: string;
			family_name?: string;
			given_name?: string;
			gender?: string;
			zoneinfo?: string;
			locale?: string;
			updated_at?: number;
			email?: string;
			phone_number?: string;
			address?: UserAddress;
		};
		const userProfile: RoomsUserProfile = {
			sub: claims.sub ?? "",
			aud: claims.aud ?? "",
			exp: claims.exp ?? expiresIn,
			iat: claims.iat ?? Date.now() / 1000,
			iss: claims.iss ?? "",
			name: standardClaims.name ?? "",
			family_name: standardClaims.family_name ?? "",
			given_name: standardClaims.given_name ?? "",
			gender: standardClaims.gender ?? "",
			zoneinfo: standardClaims.zoneinfo ?? "",
			locale: standardClaims.locale ?? "",
			updated_at: standardClaims.updated_at,
			email: standardClaims.email ?? "",
			phone_number: standardClaims.phone_number ?? "",
			address: standardClaims.address ?? {
				formatted: "",
				street_address: "",
				street_address2: "",
				street_address3: "",
				locality: "",
				postal_code: "",
				country: "",
			},
			additional_addresses: customClaims.additional_addresses ?? [],
			format_Id: customClaims.format_Id ?? "",
			list_seperator: customClaims.list_seperator ?? "",
			timezone_id: customClaims.timezone_id ?? "",
			culture_lcid: customClaims.culture_lcid ?? "",
			is_imported: customClaims.is_imported ?? false,
			default_capacity: customClaims.default_capacity ?? 2,
			default_cost_units: customClaims.default_cost_units ?? [],
			default_duration: customClaims.default_duration ?? 60,
			default_locations: customClaims.default_locations ?? [],
			available_resourcetypes: customClaims.available_resourcetypes ?? [],
			company_name: customClaims.company_name ?? "",
			claimed_resources: customClaims.claimed_resources ?? [],
			is_anonymous_user: customClaims.is_anonymous_user ?? false,
			tenant_name: customClaims.tenant_name,
		};

		return userProfile;
	}

	private enrichPersonModelWithTokens(
		personModel: RoomsUserProfile,
		tokenResult: TokenResult,
		expiresAt: number,
	): PersonModelWithTokens {
		return {
			...personModel,
			access_token: tokenResult.access_token,
			refresh_token: tokenResult.refresh_token,
			token_type: tokenResult.token_type,
			expires_at: expiresAt,
			id_token: tokenResult.id_token,
		};
	}

	public isTokenValid(token?: string | null): boolean {
		if (!token) {
			return false;
		}

		try {
			const jwt = jwtDecode<{ exp: number }>(token);
			const expiration = jwt?.exp;
			const now = Date.now() / 1000;
			return expiration ? now < expiration : false;
		} catch {
			return false;
		}
	}

	public createBearerTokenHeaderValue(accessToken?: string | null): string {
		return `Bearer ${accessToken ?? ""}`;
	}

	public createAuthorizationHeader(accessToken?: string | null): HttpHeaders {
		return new HttpHeaders({
			Authorization: this.createBearerTokenHeaderValue(accessToken),
		});
	}

	/**
	 * Login with API key for either service user or regular user
	 * @param apiKey The API key (PIN)
	 * @param resourceId Optional resource ID
	 * @param cultureId Optional culture ID
	 * @param isServiceUser Whether this is a service user authentication
	 */
	loginApiKey(
		apiKey: string,
		resourceId?: number,
		cultureId?: string,
	): Observable<PersonModelWithTokens> {
		const httpClient = new HttpClient(this.http);

		let params = new HttpParams({
			fromObject: {
				grant_type: APIKEY_GRANT_TYPE,
				scope: this.userManager.settings.scope,
				client_id: this.userManager.settings.client_id,
				apiKey: encodeURIComponent(apiKey),
			},
		});

		if (resourceId) {
			params = params.set("resourceId", resourceId);
		}

		if (cultureId) {
			params = params.set("cultureId", cultureId);
		}

		const tokenEndpoint = this.userManager.metadataService.getTokenEndpoint(false);
		return from(tokenEndpoint).pipe(
			switchMap((tokenEndpoint) =>
				httpClient.post<TokenResult>(tokenEndpoint, params.toString(), {
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
				}),
			),
			switchMap((tokenResult) => {
				// Decode the access token to get the exp claim if available
				const decodedToken = jwtDecode<JwtClaims>(tokenResult.access_token);
				const expiresAt = decodedToken.exp || 0;

				return this.fetchUserInfo(tokenResult, httpClient).pipe(
					map((personModel) =>
						this.enrichPersonModelWithTokens(personModel, tokenResult, expiresAt),
					),
				);
			}),
			catchError((error) => {
				console.error("Error during API key authentication:", error);
				throw error;
			}),
		);
	}

	private fetchUserInfo(result: TokenResult, httpClient: HttpClient): Observable<RoomsUserProfile> {
		const userInfoEndpoint = this.userManager.metadataService.getUserInfoEndpoint();

		return from(userInfoEndpoint).pipe(
			switchMap((userInfoEndpoint) =>
				httpClient
					.get<JwtClaims>(userInfoEndpoint, {
						headers: this.createAuthorizationHeader(result.access_token),
					})
					.pipe(
						map<JwtClaims, RoomsUserProfile>((claims) => {
							// Add the access token to the claims so it's available for global rights extraction
							claims.access_token = result.access_token;
							const userProfile = this.convertClaimsToUserProfile(claims, result.expires_in);
							return userProfile;
						}),
						catchError((error) => {
							console.error("Error fetching user info:", error);
							throw error;
						}),
					),
			),
		);
	}

	/**
	 * Creates a User object from a token result by decoding the access token
	 * and extracting its claims
	 * @param tokenResult The token result from the authentication endpoint
	 * @returns A User object with the token information and decoded claims
	 */
	public createUserFromTokenResult(tokenResult: TokenResult): User {
		// Decode the access token to get the claims
		const decodedToken = jwtDecode<JwtClaims>(tokenResult.access_token);

		// Ensure we have all required fields for IdTokenClaims
		const profile = {
			...decodedToken,
			// Ensure required fields have non-optional values
			sub: decodedToken.sub || "system_person",
			iss: decodedToken.iss || this.userManager.settings.authority || "",
			aud: decodedToken.aud || this.userManager.settings.client_id,
			exp: decodedToken.exp || 0,
			iat: decodedToken.iat || Math.floor(Date.now() / 1000),
		};

		// Create a user object from the token result
		return new User({
			access_token: tokenResult.access_token,
			refresh_token: tokenResult.refresh_token,
			token_type: tokenResult.token_type,
			scope: tokenResult.scope,
			expires_at: profile.exp,
			id_token: tokenResult.id_token,
			profile,
		});
	}

	/**
	 * Login using the anonymous grant type to get the SystemPerson token
	 * This token should be persisted and used for system operations
	 *
	 * @returns An observable of the User containing the SystemPerson token
	 */
	loginSystemPerson(userAccessToken?: string): Observable<User> {
		const httpClient = new HttpClient(this.http);

		// Build parameters for anonymous grant type request
		const params = new HttpParams({
			fromObject: {
				grant_type: APIKEY_GRANT_TYPE,
				impersonate_system_person: true,
				access_token: userAccessToken ?? "",
				scope: `${this.userManager.settings.scope} offline_access`,
				client_id: this.userManager.settings.client_id,
			},
		});

		// Request should include refresh token scope
		const requestParams = params.toString();

		// Get the token endpoint from the OIDC metadata
		const tokenEndpoint = this.userManager.metadataService.getTokenEndpoint(false);
		return from(tokenEndpoint).pipe(
			switchMap((tokenEndpoint) =>
				httpClient
					.post<TokenResult>(tokenEndpoint, requestParams, {
						headers: {
							"Content-Type": "application/x-www-form-urlencoded",
						},
					})
					.pipe(
						map((tokenResult) => this.createUserFromTokenResult(tokenResult)),
						catchError((error) => {
							console.error("Error authenticating as SystemPerson:", error);
							throw error;
						}),
					),
			),
		);
	}

	/**
	 * Refresh the system token using the refresh_token
	 * @param refreshToken The refresh token to use for getting a new access token
	 * @returns An observable of the new User with refreshed tokens
	 */
	refreshSystemToken(refreshToken: string): Observable<User | null> {
		const httpClient = new HttpClient(this.http);

		const params = new HttpParams({
			fromObject: {
				grant_type: "refresh_token",
				refresh_token: refreshToken,
				client_id: this.userManager.settings.client_id,
				scope: this.userManager.settings.scope,
			},
		});

		const tokenEndpoint = this.userManager.metadataService.getTokenEndpoint(false);
		return from(tokenEndpoint).pipe(
			switchMap((tokenEndpoint) =>
				httpClient
					.post<TokenResult>(tokenEndpoint, params.toString(), {
						headers: {
							"Content-Type": "application/x-www-form-urlencoded",
						},
					})
					.pipe(
						map((tokenResult) => this.createUserFromTokenResult(tokenResult)),
						catchError((error) => {
							// Check if this is an expired token error
							const isExpiredToken = this.isTokenExpiredError(error);

							// Create contextual error with additional metadata
							const contextualError = new Error(
								isExpiredToken
									? "Refresh token has expired, user needs to re-authenticate"
									: `Failed to refresh system token: ${
											error instanceof HttpErrorResponse
												? `${error.status} ${error.statusText}`
												: error.message || "Unknown error"
										}`,
							);

							// Add metadata to the error
							(contextualError as any).originalError = error;
							(contextualError as any).isExpiredToken = isExpiredToken;

							// Let the caller handle the error
							return throwError(() => contextualError);
						}),
					),
			),
		);
	}

	/**
	 * Determines if an error is caused by an expired token
	 * @param error The error to check
	 * @returns True if the error indicates an expired token
	 */
	private isTokenExpiredError(error: any): boolean {
		// Check for specific error response patterns that indicate expired tokens
		if (error instanceof HttpErrorResponse) {
			// OAuth2 invalid_grant error (standard for expired tokens)
			if (error.status === 400 && error.error?.error === "invalid_grant") {
				return true;
			}

			// Unauthorized errors can also indicate expired tokens
			if (error.status === 401) {
				return true;
			}
		}

		return false;
	}
}
